import React, { useState } from 'react';
import { Button, Card, Input, Progress, Select, Space, Table, Tabs, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CheckSquareOutlined, DownloadOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { hrmsStatusColor } from '../shared/hrmsStatus';
import OnboardingTemplateModal from './OnboardingTemplateModal';
import { useRecruitList } from './shared/useRecruitList';
import { exportRows, fmtDate, parseJsonArray, stageLabel, useRecruitAccess } from './shared/recruitUi';

const FILTERS = ['tab', 'status', 'department_id'] as const;

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { canOnboard } = useRecruitAccess();
  const list = useRecruitList(FILTERS);
  const tab = list.filters.tab === 'templates' ? 'templates' : 'plans';
  const [tplModal, setTplModal] = useState<{ open: boolean; row?: any }>({ open: false });

  const params = { ...list.apiParams, tab: undefined };
  const { data, isFetching } = useQuery({
    queryKey: hrmsKeys.onboarding(params),
    queryFn: () => hrmsService.pageOnboarding(params),
    placeholderData: keepPreviousData,
    enabled: tab === 'plans',
  });
  const { data: templates = [], isFetching: tplLoading } = useQuery({
    queryKey: hrmsKeys.onboardingTemplates(),
    queryFn: () => hrmsService.listOnboardingTemplates(),
    enabled: tab === 'templates',
  });

  const exportAll = async () => {
    const all = await hrmsService.pageOnboarding({ ...params, page: 1, limit: 5000 });
    await exportRows(
      'onboarding-plans',
      [
        ['plan_number', 'Plan #'],
        ['employee_code', 'Employee #'],
        ['employee_name', 'Employee'],
        ['department_name', 'Department'],
        ['join_date', 'Join date'],
        ['probation_end_date', 'Probation ends'],
        ['progress_pct', 'Progress %'],
        ['overdue_tasks', 'Overdue tasks'],
        ['status', 'Status'],
      ],
      all.rows
    );
  };

  const columns: ColumnsType<any> = [
    { title: 'Plan #', dataIndex: 'plan_number', width: 140, render: (v: string, r) => <a onClick={() => navigate(`/hr/onboarding/${r.id}`)}>{v}</a> },
    { title: 'Employee', dataIndex: 'employee_name', ellipsis: true, render: (v, r) => `${r.employee_code ? `${r.employee_code} — ` : ''}${v || ''}` },
    { title: 'Department', dataIndex: 'department_name', width: 160, ellipsis: true },
    { title: 'Join date', dataIndex: 'join_date', key: 'join_date', width: 120, sorter: true, sortOrder: list.sortOrderFor('join_date'), render: (v) => fmtDate(v) },
    {
      title: 'Progress',
      dataIndex: 'progress_pct',
      key: 'progress_pct',
      width: 170,
      sorter: true,
      sortOrder: list.sortOrderFor('progress_pct'),
      render: (v: number) => <Progress percent={Number(v) || 0} size="small" />,
    },
    { title: 'Overdue', dataIndex: 'overdue_tasks', width: 90, render: (v) => (Number(v) > 0 ? <Tag color="red">{v}</Tag> : '—') },
    { title: 'Status', dataIndex: 'status', width: 120, render: (s: string) => <Tag color={hrmsStatusColor(s)}>{stageLabel(s)}</Tag> },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Onboarding"
          subtitle="Day-1 checklists for new hires — owners, due dates and new-hire portal"
          primaryActions={
            <Space>
              <Button icon={<CheckSquareOutlined />} onClick={() => navigate('/hr/onboarding/my-tasks')}>
                My tasks
              </Button>
              {tab === 'plans' && (
                <Button icon={<DownloadOutlined />} onClick={exportAll}>
                  Export
                </Button>
              )}
              {canOnboard && tab === 'templates' && <Button onClick={() => setTplModal({ open: true })}>New template</Button>}
              {canOnboard && (
                <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/hr/onboarding/new')}>
                  Start plan
                </Button>
              )}
            </Space>
          }
        />
      }
    >
      <Tabs
        activeKey={tab}
        onChange={(k) => list.update({ tab: k === 'templates' ? 'templates' : undefined })}
        items={[
          {
            key: 'plans',
            label: 'Plans',
            children: (
              <>
                <Card size="small" style={{ marginBottom: 12 }}>
                  <Space wrap>
                    <Input.Search allowClear placeholder="Plan, employee name or number" defaultValue={list.q} onSearch={list.setQ} style={{ width: 260 }} />
                    <Select
                      allowClear
                      placeholder="Status"
                      style={{ width: 160 }}
                      value={list.filters.status}
                      onChange={(v) => list.setFilter('status', v)}
                      options={['Open', 'InProgress', 'Completed'].map((v) => ({ value: v, label: stageLabel(v) }))}
                    />
                    <DepartmentSelect
                      allowClear
                      placeholder="Department"
                      style={{ width: 220 }}
                      value={list.filters.department_id}
                      onChange={(v) => list.setFilter('department_id', v)}
                    />
                  </Space>
                </Card>
                <Table
                  rowKey="id"
                  loading={isFetching}
                  columns={columns}
                  dataSource={data?.rows || []}
                  scroll={{ x: 1000 }}
                  pagination={list.pagination(data?.total || 0)}
                  onChange={list.onTableChange}
                />
              </>
            ),
          },
          {
            key: 'templates',
            label: 'Checklist templates',
            children: (
              <Table
                rowKey="id"
                loading={tplLoading}
                dataSource={templates}
                pagination={false}
                columns={[
                  { title: 'Code', dataIndex: 'template_code', width: 160 },
                  { title: 'Name', dataIndex: 'template_name' },
                  {
                    title: 'Applies to',
                    key: 'scope',
                    render: (_, t: any) =>
                      [t.employment_type, t.department_id ? 'Department' : null, t.position_id ? 'Position' : null].filter(Boolean).join(' · ') || 'Everyone (fallback)',
                  },
                  { title: 'Tasks', key: 'n', width: 80, render: (_, t: any) => parseJsonArray(t.tasks_json).length },
                  { title: 'Active', dataIndex: 'is_active', width: 80, render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>) },
                  ...(canOnboard
                    ? [
                        {
                          title: '',
                          key: 'e',
                          width: 80,
                          render: (_: unknown, t: any) => (
                            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => setTplModal({ open: true, row: t })}>
                              Edit
                            </Button>
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            ),
          },
        ]}
      />
      <OnboardingTemplateModal open={tplModal.open} template={tplModal.row} onClose={() => setTplModal({ open: false })} />
    </WorkspaceLayout>
  );
};

export default OnboardingPage;
